import { Component, effect, inject, Input, OnInit, signal } from '@angular/core';
import { $state } from 'app/ajs-upgraded-providers';
import { Subject, takeUntil } from 'rxjs';
import { ConfigurationManagerService, ConnectionService, EntityManagerService, LoaderService, ToolbarEventsService, UserActiveSessionManagerService } from 'src/app/core';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';
import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { AlternativeTranslate, Categories, Departments, Items, Printers } from 'tilby-models';
import { CategoriesFe, CategoriesFormGroups } from '../categories.models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CategoryDetailsFormService } from './category-details-form.service';
import { PriceListInterface } from 'src/app/features/items/utils/item.interface';
import { color_items, EncodedData } from 'src/app/features/items/utils/item.constants';
import { ItemDataOperationsService, TranslationsInputData } from 'src/app/features/items/services/item-data-operations.service';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { VariationsComponent } from '../../items-tab/item-details/item-details-tabs/item-variations-section/variations/variations.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VariationsFormService } from '../../items-tab/item-details/item-details-tabs/item-variations-section/variations/variations-form.service';
import { ZPayBridge } from 'src/app/features/digital-payments/z-pay-bridge/z-pay-bridge.model';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-category-details',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSelectModule, MatSlideToggleModule,
    MatIconModule, MatButtonModule, VariationsComponent, TrimInputDirective,
    MatTooltipModule
  ],
  templateUrl: './category-details.component.html',
  styleUrl: './category-details.component.scss',
  providers: [
  ]
})
export class CategoryDetailsComponent implements OnInit {

  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);  
  private readonly connection = inject(ConnectionService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly loaderService = inject(LoaderService);
  private readonly state = inject($state);  
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  private readonly variationsFormService = inject(VariationsFormService);
  readonly categoryFormService = inject(CategoryDetailsFormService);
  readonly itemDataOperationsService = inject(ItemDataOperationsService);

  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';
  protected isAlternativeTranslateEnabled = !!this.configurationManagerService.getPreference('items.enable_alternative_translate');
  protected isScaleCategoriesEnabled = !!this.configurationManagerService.getPreference('cashregister.scales.manage_categories');
  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");

  @Input() category!: Categories;
  printers: Printers[] = [];
  itemsList: Items[] = [];
  categoriesList: Categories[] = [];
  

  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  categoryForm!: CategoriesFormGroups;

  colorList: Array<EncodedData> = [];
  printerList: Array<Printers> = [];
  terminalList: Array<ZPayBridge.POS> = [{name: 'Nessuno', id: null, port: 0, device_type: "pos", created_at: '', last_modified_at: '', row_version: 0}];
  priceList: Array<PriceListInterface> = [];
  departmentsList!: Array<Departments> | undefined;
  
  isReady = this.itemDataCatalogService.isReady;
  categoryFormReady = this.categoryFormService.categoryFormReady;

  constructor() {
    effect(() => {
      if(this.isReady()) {
        this.printers = this.itemDataCatalogService.getDataByKey('document-printers') || [];
        this.itemsList = this.itemDataCatalogService.getDataByKey('items') || [];
        this.categoriesList = this.itemDataCatalogService.getDataByKey('categories') || [];
        this.readData();
        this.categoryForm = this.categoryFormService.createForm(this.category);
        this.loaderService.forceHide();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      if(this.categoryFormReady()) {
        this.listenToCategoryNameChanges();
      }
    })
  }

  ngOnInit(): void {
    if (!this.category) {
      this.category = {
        ...new CategoriesFe(),
        display: true,
        display_kiosk: true,
        display_slave_device: true,
      } as Categories;
    }
    this.isNew = !this.category?.id;
    this.createToolbarButtons();
    this.initContextToolbarLabel();
  }

  private readData() {
    // Costruzione lista terminali
    this.loadZPayBridgePOS();

    // Costruzione lista colori
    this.colorList = [...color_items];
    this.colorList.unshift({name: 'Nessuno', value: null});

    // Costruzione liste prezzi
    this.priceList = this.itemDataOperationsService.getPriceLists({ firstRequired: false });

    // Costruzione liste reparti
    this.departmentsList = this.itemDataCatalogService.getExtendedDepartments() || [];

    // Costruzione lista stampanti
    this.printerList = structuredClone(this.printers || []);
    this.printerList.unshift({name: 'Nessuno', id: 0, driver: '', type: ''});
  }

  async loadZPayBridgePOS() {
    this.itemDataCatalogService.loadZPayBridgePOS().then((response) => {
      this.terminalList = this.itemDataCatalogService.getDataByKey("z-pay-bridge") || [];
      this.terminalList.unshift({name: 'Nessuno', id: null, port: 0, device_type: "pos", created_at: '', last_modified_at: '', row_version: 0});
    })
  }

  initContextToolbarLabel() {
    if(this.isNew) {
      this.toolbarEventsContextService.label = `ITEMS.CATEGORIES.DETAILS.NAME_PLACEHOLDER`;
    } else {
      this.toolbarEventsContextService.label = ( this.category.name || "" ) + '\u200b';
    }
  }
  
  private listenToCategoryNameChanges() {
    this.categoryFormService.name?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.toolbarEventsContextService.label = value? value + '\u200b': `ITEMS.CATEGORIES.DETAILS.NAME_PLACEHOLDER`;
    })
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_category',
          icon: signal('data_object'),
          label: signal('ITEMS.CATEGORIES.ACTIONS.SHOW_JSON'),
          click: _ => this.openCategoryJsonDetail()
        }
      ]
    })
  }


  getSelectedColorName() {
    const colorName = this.colorList?.find(col => col.value == this.categoryFormService.color?.value)?.name;
    return colorName? this.translate.instant(colorName) : '';
  }
  
  async openCategoryJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.CATEGORIES.ACTIONS.SHOW_JSON', 
        nameFile: 'item_json_data', 
        json: this.categoryFormService.getComponentFields(this.category) } 
    });
  }
  
  back() {
    this.state.go(`app.new.items.categories`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.CATEGORIES.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
    
    this.categoryFormService.resetFormValues(this.category);
    this.variationsFormService.resetFormValues(this.category?.variations || []);
  }

  async save() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    const categoryAlreadyExist = this.categoriesList?.find((c: Categories) => 
      this.categoryFormService.name?.value === c.name && this.categoryFormService.id?.value !== c.id
    )
    
    if (categoryAlreadyExist) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.CATEGORIES.CATEGORY_ALREAD_EXISTING')
        }})
      return;
    }

    if(this.categoryFormService.form.invalid) {
      this.categoryFormService.form.markAllAsTouched();
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.CATEGORIES.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    const categoryToSend = {
      ...this.categoryFormService.getComponentFields(this.category),
      ...(this.category?.id && {id: this.category.id})
    } as Categories;

    try {
      if(categoryToSend.id) {
        this.category = await this.entityManager.categories.putOneOnline(categoryToSend);
      } else {
        this.category = await this.entityManager.categories.postOneOnline(categoryToSend);
      }

      // Aggiornamento lista categorie
      // const currentCategories = this.itemDataCatalogService.getDataByKey('categories') || [];

      // if (categoryToSend.id) {
      //   const index = currentCategories.findIndex(c => c.id === this.category.id);
      //   if (index !== -1) {
      //     currentCategories[index] = { ...this.category};
      //   }
      // } else {
      //   currentCategories.push({ ...this.category});
      // }

      // this.itemDataCatalogService.addDataToCatalog('categories', currentCategories);
      // this.itemDataCatalogService.updateFilteredDataToCatalog('categories', currentCategories);

      this.state.go(`app.new.items.categories`);

      this._snackBar.open(this.translate.instant('ITEMS.CATEGORIES.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: categoryToSend.id ? 'ITEMS.CATEGORIES.ERROR_IN_EDIT' : 'ITEMS.CATEGORIES.ERROR_IN_CREATION'
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  manageAlternativeTranslate() {
    const config: TranslationsInputData = {
      mainValue: this.categoryFormService.name?.value,
      currentValues: this.categoryFormService.alternativeNames?.value || [],
      maxLength: this.categoryFormService.validators.nameMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.categoryFormService.alternativeNames?.setValue(updatedValues);
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.categoryFormReady.set(false);
  }
}
